<template>
    <div v-if="'description' in event" class="attendance-page">
        <v-card class="attendance-header">
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <h6 class="text-h6 font-weight-bold">
                            {{ event.description }} - {{ moment(event.dutyDate).format("D-MMM-yyyy") }}<br />
                            <small><span class="text-red">
                                    Reporting Time: {{ moment(event.reportingTime).format("hh:mm A") }}
                                </span></small><br />
                            <small>
                                <span class="text-red">
                                    Scanning from: {{ moment(event.scanningStart).format("hh:mm A") }}
                                    to {{ moment(event.scanningEnd).format("hh:mm A") }}
                                </span>
                            </small>
                        </h6>
                    </v-col>
                    <v-col cols="12" md="6" align="end">
                        <v-btn @click="showReportDialog">Download Report</v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card class="attendance-list-card">
            <div class="attendance-list-inner">
                <div class="attendance-search">
                    <v-text-field v-model="searchQuery" label="Search attendance" prepend-inner-icon="mdi-magnify"
                        clearable hide-details density="compact" placeholder="ITS, name, rank, team, section..." />
                </div>
                <div class="attendance-list-scroll">
                    <v-list density="compact" v-if="filteredAttendance.length > 0">
                        <v-list-item density="compact" v-for="(att, ix) in filteredAttendance"
                            :key="`${att.itsId ?? getAttendanceMember(att).itsId}-${ix}`"
                            :title="`${ix + 1} - ${getAttendanceMember(att).itsId ?? att.itsId} - ${getAttendanceMember(att).fullName} - ${getAttendanceMember(att).rank} (${getAttendanceMember(att).organization})`"
                            :subtitle="formatTime(att.reportingTime)"
                            :prepend-avatar="getAttendanceMember(att).pictureInUniform">
                        </v-list-item>
                    </v-list>
                    <h4 v-else-if="attendance.length > 0" class="text-center pa-4">No matching members</h4>
                    <h4 v-else class="text-center pa-4">No Attendance Marked</h4>
                </div>
            </div>
        </v-card>

        <v-card class="attendance-input-bar">
            <v-card-text>
                <v-text-field v-model="selectedMemberId" label="Enter Its Number" @keydown.enter="addMember"
                    hide-details />
            </v-card-text>
        </v-card>
    </div>

    <simple-dialog ref="dlgReport" width="450px" confirmButtonText="Download" @ok="downloadReport">
        <template v-slot:content>
            <VueDatePicker v-model="reportParams.startDate" time-picker-inline disable-year-select
                            :enable-time-picker="false" auto-apply format="dd-MMM-yyyy"
                            :text-input="{ format: 'dd-MMM-yyyy' }" model-type="dd-MMM-yyyy" placeholder="dd-MMM-yyyy" />

            <VueDatePicker v-model="reportParams.endDate" time-picker-inline disable-year-select
                            :enable-time-picker="false" auto-apply format="dd-MMM-yyyy"
                            :text-input="{ format: 'dd-MMM-yyyy' }" model-type="dd-MMM-yyyy" placeholder="dd-MMM-yyyy" class="mt-3" />
        </template>
    </simple-dialog>
</template>

<script setup>
import { ref, inject, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute } from "vue-router";
import * as atService from '@/services/attendanceService';
import moment from 'moment';
import debounce from 'lodash/debounce';

const loader = inject("loader");
const snackbar = inject("snackBar");
const router = useRoute();

const attendanceId = router.params.id;
const event = ref({});
const attendance = ref([]);
const searchQuery = ref("");
const debouncedSearchQuery = ref("");
const selectedMemberId = ref("");
const dlgReport = ref(null);

const applyDebouncedSearch = debounce((value) => {
    debouncedSearchQuery.value = value;
}, 800);

watch(searchQuery, (value) => {
    if (!value?.trim()) {
        applyDebouncedSearch.cancel();
        debouncedSearchQuery.value = "";
        return;
    }

    applyDebouncedSearch(value);
});

const filteredAttendance = computed(() => {
    const query = debouncedSearchQuery.value?.trim().toLowerCase();
    if (!query)
        return attendance.value;

    const tokens = query.split(/\s+/).filter(Boolean);
    return attendance.value.filter((att) => matchesAttendanceSearch(att, tokens));
});

const reportParams = ref({
    startDate: new Date(),
    endDate: new Date(),
});

function formatTime(dt) {
    return moment(dt).format("DD-MMM-YYYY hh:mm A");
}

function getAttendanceMember(att) {
    return att?.member ?? att?.Member ?? {};
}

function getAttendanceSearchText(att) {
    const m = getAttendanceMember(att);
    const fields = [
        att?.itsId,
        m.itsId,
        m.fullName,
        m.rank,
        m.organization,
        m.section,
        m.status,
        m.mobile,
        m.mobileNo,
        m.whatsapp,
        m.email,
    ];

    if (att?.reportingTime) {
        fields.push(formatTime(att.reportingTime));
        fields.push(moment(att.reportingTime).format("hh:mm A"));
        fields.push(moment(att.reportingTime).format("HH:mm"));
    }

    return fields
        .filter((value) => value != null && value !== "")
        .map((value) => String(value).toLowerCase())
        .join(" ");
}

function matchesAttendanceSearch(att, tokens) {
    const haystack = getAttendanceSearchText(att);
    return tokens.every((token) => haystack.includes(token));
}

function normalizeAttendanceList(result) {
    if (Array.isArray(result))
        return result;

    if (result?.$values)
        return result.$values;

    return [];
}

async function getData() {
    loader.show();

    let result = await atService.master_getById(attendanceId)
        .catch(error => console.log(error))
        .finally(async () => {
            await getDetail();
            loader.hide();
        });

    event.value = result;
}

async function getDetail() {
    let result = await atService.detail_getAllByMasterId(attendanceId)
        .catch(error => console.log(error))

    if (result)
        attendance.value = normalizeAttendanceList(result);
}

async function addMember() {
    if (selectedMemberId.value == null) {
        snackbar.error("Please enter an Its number");
        return;
    }

    loader.show();

    await atService.detail_add(attendanceId, { itsId: selectedMemberId.value })
        .then(async () => {
            snackbar.success("Attendance has been marked successfully")
            await getDetail();
        })
        .catch(error => {
            snackbar.error(error);
            console.log(error);
        })
        .finally(() => {
            loader.hide();
        });

    selectedMemberId.value = null;
}

async function showReportDialog() {
    dlgReport.value.show();
}

async function downloadReport() {
    console.log(reportParams.value);

    loader.show();
    
    const result = await atService.report_detail(attendanceId, reportParams.value)
    .catch(ex => snackbar.error(ex));
    
    loader.hide();
    
    // Create a URL for the file blob
    const url = window.URL.createObjectURL(new Blob([result]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `attendance_${attendanceId}.xlsx`); // Replace with your desired file name
    document.body.appendChild(link);
    link.click();

    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
}

onMounted(async () => {
    await getData();

    console.log(reportParams.value);
});

onUnmounted(() => {
    applyDebouncedSearch.cancel();
});

</script>

<style scoped>
.attendance-page {
    display: flex;
    flex-direction: column;
    height: calc(100vh - var(--v-layout-top) - 48px);
    min-height: 320px;
    overflow: hidden;
    gap: 12px;
}

.attendance-header {
    flex-shrink: 0;
}

.attendance-list-card {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.attendance-list-card :deep(.v-card__content) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.attendance-list-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.attendance-search {
    flex-shrink: 0;
    padding: 8px 12px 0;
}

.attendance-list-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
}

.attendance-input-bar {
    flex-shrink: 0;
}
</style>
